import type { QueryClient } from '@tanstack/react-query';

const pendingInitialRefreshes = new WeakSet<Promise<unknown>>();

export function isEmailWorkbenchData(data: unknown): boolean {
  const category = (data as { pulse?: { category?: string } } | undefined)?.pulse?.category;
  return category === 'email_in' || category === 'email_out';
}

/**
 * Attachment workers send identity-only events, not authoritative HTML.
 * Refetch only already-cached email workbenches through the ACL-checked endpoint.
 * Inactive entries are marked stale, not fetched until opened.
 */
export function invalidateEmailWorkbenches(client: QueryClient, pulseId?: number): void {
  const matchesWorkbench = (query: { queryKey: readonly unknown[] }) =>
    query.queryKey[0] === 'pulse-workbench'
    && (pulseId === undefined || query.queryKey[1] === pulseId);

  // The first response may already contain pre-worker HTML, but hasn't exposed
  // its category yet. Invalidating now would just deduplicate against that
  // request. Remember the poke until it settles, then refresh only if it turned
  // out to be an email. Coalesce repeated pokes for the same initial request.
  for (const query of client.getQueryCache().findAll({ predicate: matchesWorkbench })) {
    const initialRequest = query.state.data === undefined && query.state.fetchStatus !== 'idle'
      ? query.promise : undefined;
    if (!initialRequest || pendingInitialRefreshes.has(initialRequest)) continue;
    pendingInitialRefreshes.add(initialRequest);
    void initialRequest.then(() => {
      // A logout/cache clear may have removed or replaced this query meanwhile.
      if (client.getQueryCache().get(query.queryHash) !== query) return;
      if (isEmailWorkbenchData(query.state.data)) {
        void client.invalidateQueries({ queryKey: query.queryKey, exact: true });
      }
    }, () => {
      // A failed initial read already surfaces its error; don't retry denied
      // access or resurrect a cancelled/removed query on behalf of a broadcast.
    });
  }

  void client.invalidateQueries({
    predicate: query => matchesWorkbench(query) && isEmailWorkbenchData(query.state.data),
  });
}